import { useQuery } from '@tanstack/react-query';
import { Card, Center, Segmented, Spinner } from '@barev/ui';
import { useI18n, useTRPC, yerevanDateKey } from '@barev/web';
import { useMemo, useState } from 'react';
import { JobRow, PageHead } from '../lib.tsx';

type Period = 'week' | 'month' | 'prevMonth';

function range(p: Period): { from: string; to: string } {
  const today = yerevanDateKey(new Date());
  const [y, m] = today.split('-').map(Number) as [number, number];
  const pad = (n: number) => String(n).padStart(2, '0');
  if (p === 'week') return { from: yerevanDateKey(new Date(Date.now() - 6 * 86_400_000)), to: today };
  if (p === 'month') return { from: `${y}-${pad(m)}-01`, to: today };
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
  return { from: `${py}-${pad(pm)}-01`, to: `${py}-${pad(pm)}-${pad(last)}` };
}

/** PRO-6: история и заработок за период */
export function History() {
  const { t, amd, dt } = useI18n();
  const trpc = useTRPC();
  const [period, setPeriod] = useState<Period>('week');
  const r = useMemo(() => range(period), [period]);
  const history = useQuery(trpc.pro.history.queryOptions(r));
  return (
    <main className="app">
      <PageHead title={t('pro.history.title')} back={false} />
      <Segmented
        value={period}
        onChange={setPeriod}
        options={[
          { value: 'week', label: t('pro.history.week') },
          { value: 'month', label: t('pro.history.month') },
          { value: 'prevMonth', label: t('pro.history.prevMonth') },
        ]}
      />
      {!history.data ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <>
          <Card tone="dark" className="bc-between">
            <div>
              <div className="bc-small" style={{ color: '#CFC8C2' }}>
                {t('pro.history.earned')}
              </div>
              <div className="bc-num" style={{ fontSize: 30 }}>
                {amd(history.data.totalEarned)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="bc-small" style={{ color: '#CFC8C2' }}>
                {t('pro.history.done')}
              </div>
              <div className="bc-num" style={{ fontSize: 30 }}>
                {history.data.doneCount}
              </div>
            </div>
          </Card>
          <div className="bc-small bc-muted">
            {history.data.rate.kind === 'preset'
              ? t('pro.history.ratePreset', { name: history.data.rate.name })
              : history.data.rate.value == null
                ? t('pro.history.ratePartner')
                : history.data.rate.type === 'PERCENT'
                  ? t('pro.history.ratePercent', { value: history.data.rate.value })
                  : t('pro.history.rateFixed', { value: amd(history.data.rate.value) })}
          </div>
          {history.data.orders.length === 0 ? (
            <p className="bc-muted">{t('pro.history.empty')}</p>
          ) : (
            history.data.orders.map((o) => (
              <div key={o.id}>
                <div className="bc-small bc-muted" style={{ margin: '4px 4px' }}>
                  {dt(o.scheduledAt, { weekday: 'short', day: 'numeric', month: 'long' })}
                </div>
                <JobRow j={o} href={`#/orders/${o.id}`} right={o.payout > 0 ? <span className="bc-num">{amd(o.payout)}</span> : undefined} />
              </div>
            ))
          )}
        </>
      )}
    </main>
  );
}
